import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { cn } from '../../../components/ui/_cn';

// The panel's distance from the screen's edges and from the icon, and its
// widest. The gutter is the page's own 16px phone gutter.
const GUTTER = 16;
const GAP = 6;
const MAX_WIDTH = 300;
// The `card` variant (Overview's balance card tooltips, frame 1a) is roomier.
const CARD_MAX_WIDTH = 340;

const PANEL_CLASS = {
  note: 'fixed z-50 block w-max rounded-lg border border-fin-border bg-fin-surface px-3 py-2 text-left text-[12.5px] text-fin-ink-2 shadow',
  card: 'fixed z-50 block w-max rounded-xl border border-fin-border bg-fin-surface px-5 py-[18px] text-left text-[13.5px] leading-[1.55] text-fin-ink-2 shadow-lg',
};

// Where the panel goes, in viewport pixels, given the icon's rect, the
// panel's measured size and the viewport. Centred under the icon, then slid
// sideways just enough to keep a gutter on both sides; below the icon unless
// only above has room for it.
export const placePanel = (anchor, panel, viewport) => {
  const centre = anchor.left + anchor.width / 2;
  const left = Math.max(GUTTER, Math.min(centre - panel.width / 2, viewport.width - GUTTER - panel.width));
  const below = anchor.bottom + GAP;
  const above = anchor.top - GAP - panel.height;
  const fitsBelow = below + panel.height <= viewport.height - GUTTER;
  return { left, top: fitsBelow || above < GUTTER ? below : above };
};

// The info icon and its panel (spec 2026-09-22 §E): the balance card's In
// transit and Processing rows (variant `card`) and the note-style hints
// elsewhere in Finance (variant `note`). It replaced the bare `title=`
// attribute a screen reader and a touch device both ignore.
// Opens on hover, keyboard focus AND click. Opened by the pointer, it closes
// when the pointer leaves the icon (owner report 2026-10-09: on Payout
// Details, running the pointer down the Summary's icons left every panel
// open, stacked on each other). Opened by the keyboard, it stays until focus
// leaves the icon. Escape and an outside click close it either way, and a
// touch reader's tap is closed by the next tap elsewhere.
// No transition of any kind, so `prefers-reduced-motion` is trivially
// respected rather than special-cased.
//
// The panel is drawn on document.body at a fixed position (owner report
// 2026-09-23): inside the balance card it was clipped by the card's rounded
// `overflow-hidden` edge, cut off on the left and at the bottom. Out there
// nothing clips it, and `placePanel` keeps it on screen instead.
const FinInfoPopover = ({ text, ariaLabel = 'More information', className, variant = 'note' }) => {
  const [open, setOpen] = useState(false);
  const [place, setPlace] = useState(null);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const panelId = useId();
  // Whether the pointer is over the icon, and whether the panel was opened
  // by focus that did not come with the pointer (a mouse click focuses the
  // button too, but the pointer is over it by then).
  const hovered = useRef(false);
  const viaKeyboard = useRef(false);

  useEffect(() => {
    if (!open) viaKeyboard.current = false;
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    // The panel is not inside rootRef in the DOM any more, so a click on it
    // has to be told apart from a click elsewhere explicitly.
    const onPointerDown = (event) => {
      const inside = [rootRef.current, panelRef.current].some((node) => node && node.contains(event.target));
      if (!inside) setOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [open]);

  // Measured before paint, so the panel is never seen in the wrong place;
  // re-measured on any scroll or resize while open, because a fixed panel
  // does not move with the page on its own.
  useLayoutEffect(() => {
    if (!open) {
      setPlace(null);
      return undefined;
    }
    const measure = () => {
      if (!triggerRef.current || !panelRef.current) return;
      const panel = panelRef.current.getBoundingClientRect();
      setPlace(
        placePanel(
          triggerRef.current.getBoundingClientRect(),
          { width: panel.width, height: panel.height },
          { width: window.innerWidth, height: window.innerHeight },
        ),
      );
    };
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [open]);

  const cap = variant === 'card' ? CARD_MAX_WIDTH : MAX_WIDTH;
  const maxWidth = typeof window === 'undefined' ? cap : Math.min(cap, window.innerWidth - GUTTER * 2);

  return (
    <span ref={rootRef} className={cn('relative inline-flex', className)}>
      <button
        ref={triggerRef}
        type="button"
        aria-label={ariaLabel}
        aria-describedby={open ? panelId : undefined}
        onMouseEnter={() => {
          hovered.current = true;
          setOpen(true);
        }}
        onMouseLeave={() => {
          hovered.current = false;
          if (!viaKeyboard.current) setOpen(false);
        }}
        onFocus={() => {
          if (!hovered.current) viaKeyboard.current = true;
          setOpen(true);
        }}
        onBlur={() => {
          if (viaKeyboard.current) setOpen(false);
        }}
        onClick={() => setOpen(true)}
        className="cursor-help text-fin-faintest"
      >
        <InformationCircleIcon className="h-3.5 w-3.5 stroke-[2.2]" aria-hidden="true" />
      </button>
      {open
        ? createPortal(
            <span
              ref={panelRef}
              id={panelId}
              role="tooltip"
              style={{
                maxWidth,
                left: place ? place.left : 0,
                top: place ? place.top : 0,
                visibility: place ? 'visible' : 'hidden',
              }}
              className={PANEL_CLASS[variant] || PANEL_CLASS.note}
            >
              {text}
            </span>,
            document.body,
          )
        : null}
    </span>
  );
};

export default FinInfoPopover;
